import { cn } from '@/lib/shadcn/utils';
import type { CandidateFilter } from '@/modules/hiring-manager/hooks/useCandidatePipelineQuery';

const filters: { key: CandidateFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'inProgress', label: 'In progress' },
  { key: 'waiting', label: 'Needs your decision' },
  { key: 'interviewing', label: 'Interviewing' },
  { key: 'hired', label: 'Hired' },
  { key: 'passed', label: 'Passed' },
];

interface CandidateFilterBarProps {
  active: CandidateFilter;
  counts: Record<CandidateFilter, number | undefined>;
  onChange: (filter: CandidateFilter) => void;
}

/**
 * Quiet text buttons with counts; the header tabs already use the underline.
 * One line that scrolls sideways when narrow (a phone, or beside the
 * candidate panel) rather than wrapping a filter onto a line of its own; the
 * padding keeps focus rings clear of the scroll clip.
 */
export function CandidateFilterBar({
  active,
  counts,
  onChange,
}: CandidateFilterBarProps) {
  return (
    <div
      aria-label="Candidate filter"
      className="-m-1 flex items-center gap-1 overflow-x-auto p-1 [scrollbar-width:none]"
      role="group"
    >
      {filters.map((filter) => {
        const isCurrent = filter.key === active;
        return (
          <button
            aria-pressed={isCurrent}
            className={cn(
              'focus-visible:ring-ring/50 inline-flex h-7 shrink-0 cursor-pointer items-center gap-1.5 rounded-md px-2 text-[13px] font-medium whitespace-nowrap transition-colors duration-100 outline-none focus-visible:ring-[3px]',
              isCurrent
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
            key={filter.key}
            onClick={() => onChange(filter.key)}
            type="button"
          >
            {filter.label}
            {/* Holds a digit's room while counts load, so chips don't shift under the cursor. */}
            <span className="min-w-[1ch] text-xs text-neutral-500 tabular-nums">
              {counts[filter.key]}
            </span>
          </button>
        );
      })}
    </div>
  );
}
